"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AttachmentField = void 0;
const joi_1 = __importDefault(require("joi"));
const field_1 = require("./field");
const view_types_1 = require("../../types/view_types");
const lodash_1 = require("lodash");
const datasheet_1 = require("../datasheet");
const field_types_1 = require("../../types/field_types");
const utils_1 = require("../../utils");
const i18n_1 = require("../../exports/i18n");
const utils_2 = require("../../model/utils");
const validate_schema_1 = require("./validate_schema");
const baseAttachmentFieldSchema = {
    id: joi_1.default.string().required(),
    name: joi_1.default.string().required(),
    mimeType: joi_1.default.string().required(),
    token: joi_1.default.string().required(),
    bucket: joi_1.default.string().required(),
    size: joi_1.default.number().required(),
    width: joi_1.default.number(),
    height: joi_1.default.number(),
    preview: joi_1.default.string()
};
class AttachmentField extends field_1.ArrayValueField {
    constructor(field, state) {
        super(field, state);
        this.field = field;
    }
    get apiMetaProperty() {
        return null;
    }
    get openValueJsonSchema() {
        return {
            type: 'array',
            title: this.field.name,
            items: {
                type: 'object',
                properties: {
                    id: {
                        type: 'string',
                        title: (0, i18n_1.t)(i18n_1.Strings.robot_variables_join_attachment_IDs),
                    },
                    name: {
                        type: 'string',
                        title: (0, i18n_1.t)(i18n_1.Strings.robot_variables_join_attachment_names),
                    },
                    mimeType: {
                        type: 'string',
                        title: (0, i18n_1.t)(i18n_1.Strings.robot_variables_join_attachment_mime_types),
                    },
                    token: {
                        type: 'string',
                        title: (0, i18n_1.t)(i18n_1.Strings.robot_variables_join_attachment_upload_token),
                    },
                    bucket: {
                        type: 'string',
                        title: (0, i18n_1.t)(i18n_1.Strings.robot_variables_join_attachment_storage_locations),
                    },
                    size: {
                        type: 'number',
                        title: (0, i18n_1.t)(i18n_1.Strings.robot_variables_join_attachment_sizes),
                    },
                    width: {
                        type: 'number',
                        title: (0, i18n_1.t)(i18n_1.Strings.robot_variables_join_attachment_widths)
                    },
                    height: {
                        type: 'number',
                        title: (0, i18n_1.t)(i18n_1.Strings.robot_variables_join_attachment_heights)
                    },
                    preview: {
                        type: 'string',
                        title: (0, i18n_1.t)(i18n_1.Strings.robot_variables_join_attachment_preview_image_token),
                    },
                    previewUrl: {
                        type: 'string',
                        title: (0, i18n_1.t)(i18n_1.Strings.robot_variables_join_attachment_thumbnail_URLs),
                    },
                    url: {
                        type: 'string',
                        title: (0, i18n_1.t)(i18n_1.Strings.robot_variables_join_attachment_URLs),
                    }
                }
            }
        };
    }
    get canGroup() {
        return false;
    }
    get acceptFilterOperators() {
        return [
            view_types_1.FOperator.IsEmpty,
            view_types_1.FOperator.IsNotEmpty,
        ];
    }
    get basicValueType() {
        return field_types_1.BasicValueType.Array;
    }
    get innerBasicValueType() {
        return field_types_1.BasicValueType.String;
    }
    static createDefault(fieldMap) {
        return {
            id: datasheet_1.DatasheetActions.getNewFieldId(fieldMap),
            type: field_types_1.FieldType.Attachment,
            name: datasheet_1.DatasheetActions.getDefaultFieldName(fieldMap),
            property: null,
        };
    }
    validateProperty() {
        return AttachmentField.propertySchema.validate(this.field.property);
    }
    validateCellValue(cv) {
        return AttachmentField.cellValueSchema.validate(cv);
    }
    validateOpenWriteValue(owv) {
        return AttachmentField.openWriteValueValueSchema.validate(owv);
    }
    static defaultProperty() {
        return null;
    }
    eq(cv1, cv2) {
        if (cv1 == null || cv2 == null) {
            return cv1 === cv2;
        }
        return (0, lodash_1.isEqual)([cv1].flat().map(item => item.token), [cv2].flat().map(item => item.token));
    }
    validate(value) {
        return (0, lodash_1.isArray)(value) && value.every((attachment) => {
            return Boolean(attachment &&
                (0, lodash_1.isString)(attachment.id) &&
                (0, lodash_1.isString)(attachment.bucket) &&
                (0, lodash_1.isString)(attachment.token) &&
                (0, lodash_1.isString)(attachment.mimeType) &&
                (0, lodash_1.isString)(attachment.name) &&
                (0, lodash_1.isNumber)(attachment.size));
        });
    }
    cellValueToArray(cellValue) {
        if (this.validate(cellValue)) {
            return cellValue.map(cur => {
                const filePath = (0, utils_1.cellValueToImageSrc)(cur);
                return `${cur.name} (${filePath})`;
            });
        }
        return null;
    }
    // most of arrayValueToString's field just join, except number dataTime's with format
    arrayValueToString(cellValues) {
        return cellValues && cellValues.length ? cellValues.join(', ') : null;
    }
    cellValueToString(cellValue) {
        return this.arrayValueToString(this.cellValueToArray(cellValue));
    }
    cellValueToStdValue(val) {
        const stdVal = {
            sourceType: field_types_1.FieldType.Attachment,
            data: [],
        };
        if (val != null) {
            stdVal.data = val.map(attach => {
                return Object.assign({ text: this.cellValueToString([attach]) || '' }, attach);
            });
        }
        return stdVal;
    }
    stdValueToCellValue(stdVal) {
        if (stdVal.data.length === 0) {
            return null;
        }
        const cellValue = stdVal.data.map(val => {
            const { text } = val, v = __rest(val, ["text"]);
            console.log(text);
            return v;
        });
        if (this.validate(cellValue)) {
            return cellValue;
        }
        return null;
    }
    defaultValueForCondition(_condition) {
        return null;
    }
    cellValueToApiStandardValue(cellValue) {
        if (this.validate(cellValue)) {
            return cellValue.map(value => {
                return {
                    id: value.id,
                    name: value.name,
                    size: value.size,
                    mimeType: value.mimeType,
                    token: value.token,
                    width: value === null || value === void 0 ? void 0 : value.width,
                    height: value === null || value === void 0 ? void 0 : value.height,
                    preview: value.preview ? (0, utils_1.cellValueToImageSrc)(value, { isPreview: true }) : undefined,
                    url: (0, utils_1.cellValueToImageSrc)(value),
                };
            });
        }
        return null;
    }
    cellValueToApiStringValue(cellValue) {
        return this.cellValueToString(cellValue);
    }
    cellValueToOpenValue(cellValue) {
        if (!(0, utils_2.isNullValue)(cellValue)) {
            return cellValue.map(value => {
                return Object.assign(Object.assign({}, value), { 
                    // addon value
                    previewUrl: value.preview ? (0, utils_1.cellValueToImageSrc)(value, { isPreview: true }) : undefined, url: (0, utils_1.cellValueToImageSrc)(value) });
            });
        }
        return null;
    }
    openWriteValueToCellValue(openWriteValue) {
        if ((0, utils_2.isNullValue)(openWriteValue)) {
            return null;
        }
        return openWriteValue.map(v => ({
            id: v.id,
            name: v.name,
            mimeType: v.mimeType,
            token: v.token,
            bucket: v.bucket,
            size: v.size,
            width: v === null || v === void 0 ? void 0 : v.width,
            height: v === null || v === void 0 ? void 0 : v.height,
            preview: v === null || v === void 0 ? void 0 : v.preview
        }));
    }
    validateAddOpenFieldProperty(updateProperty) {
        if (updateProperty === null) {
            return { error: undefined, value: null };
        }
        return this.validateUpdateOpenProperty(updateProperty);
    }
    filterValueToOpenFilterValue() {
        return null;
    }
    openFilterValueToFilterValue() {
        return null;
    }
    validateOpenFilterValue(value) {
        if (value) {
            return (0, validate_schema_1.joiErrorResult)('The attachment field does not need to set the filter criteria value');
        }
        return joi_1.default.allow(null).validate(value);
    }
}
exports.AttachmentField = AttachmentField;
AttachmentField.propertySchema = joi_1.default.equal(null);
AttachmentField.cellValueSchema = joi_1.default.array().items(joi_1.default.object(baseAttachmentFieldSchema).required()).allow(null).required();
AttachmentField.openWriteValueValueSchema = joi_1.default.array().items(joi_1.default.object(Object.assign(Object.assign({}, baseAttachmentFieldSchema), { url: joi_1.default.string(), previewUrl: joi_1.default.string() })).required()).allow(null).required();
//# sourceMappingURL=attachment_field.js.map